import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Divider,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ApiIcon from '@mui/icons-material/Api';
import BusinessIcon from '@mui/icons-material/Business';
import TuneIcon from '@mui/icons-material/Tune';
import apiClient from './Api';

// Import components
import Branches from './Branches';
import Variables from './Variables';

function AdminConfigurations() {
  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [companyBranches, setCompanyBranches] = useState([]);
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await apiClient.getBranches();
        const branches = Array.isArray(response) ? response : response.results || [];
        setCompanyBranches(branches);
      } catch (error) {
        console.error('Error fetching branches:', error);
        showNotification('Error loading branches', 'error');
      }
    };
    
    fetchBranches();
  }, []);

  // API Configurations Section
  const [apiConfigs, setApiConfigs] = useState([]);

  // useEffect to fetch API configurations:
  useEffect(() => {
    const fetchAPIConfigs = async () => {
      try {
        const response = await apiClient.getAPIConfigurations();
        const configs = Array.isArray(response) ? response : response.results || [];
        // Transform the data to match your current structure  
        const transformedConfigs = configs.map(config => ({
          id: config.id,
          name: config.name,
          branchId: config.branch_id || 'all',
          fields: config.fields.map(field => ({
            name: field.field_name,
            value: field.field_value,
            visible: false
          }))
        }));
        setApiConfigs(transformedConfigs);
      } catch (error) {
        console.error('Error fetching API configurations:', error);
        showNotification('Error loading API configurations', 'error');
      }
    };
    
    fetchAPIConfigs();
  }, []);
  
  const [isEditingAPI, setIsEditingAPI] = useState(false);
  const [originalApiConfigs, setOriginalApiConfigs] = useState([]);
  const [openAddApiDialog, setOpenAddApiDialog] = useState(false);
  const [newApiConfig, setNewApiConfig] = useState({
    name: '',
    branchId: 'all',
    fields: [{ name: 'API Key', value: '', visible: false }]
  });

  // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Helper function to get branch name by ID
  const getBranchName = (branchId) => {
    if (branchId === 'all') return 'All Branches';
    const branch = companyBranches.find(b => b.id === branchId);
    return branch ? branch.name : 'Unknown Branch';
  };

  // API Configurations Functions
  const handleEditAPI = () => {
    setOriginalApiConfigs(JSON.parse(JSON.stringify(apiConfigs)));
    setIsEditingAPI(true);
  };

  const handleCancelAPI = () => {
    setApiConfigs(JSON.parse(JSON.stringify(originalApiConfigs)));
    setIsEditingAPI(false);
  };

  const handleSaveAPISettings = async () => {
    try {
      // Save each modified API configuration
      for (const config of apiConfigs) {
        const apiData = {
          name: config.name,
          branch_id: config.branchId === 'all' ? null : config.branchId,
          fields: config.fields.map(field => ({
            field_name: field.name,
            field_value: field.value
          }))
        };
        
        if (config.id && config.id > 0) {
          // Update existing
          await apiClient.updateAPIConfiguration(config.id, apiData);
        } else {
          // Create new (shouldn't happen in edit mode, but safety check)
          await apiClient.createAPIConfiguration(apiData);
        }
      }
      
      setIsEditingAPI(false);
      showNotification('API configurations saved successfully');
    } catch (error) {
      console.error('Error saving API configurations:', error);
      showNotification('Error saving API configurations', 'error');
    }
  };

  const handleApiVisibility = (configIndex, fieldIndex) => {
    const updatedApiConfigs = [...apiConfigs];
    updatedApiConfigs[configIndex].fields[fieldIndex].visible = 
      !updatedApiConfigs[configIndex].fields[fieldIndex].visible;
    setApiConfigs(updatedApiConfigs);
  };

  const handleDeleteApiConfig = async (id) => {
    try {
      await apiClient.deleteAPIConfiguration(id);
      setApiConfigs(apiConfigs.filter(config => config.id !== id));
      showNotification('API configuration deleted');
    } catch (error) {
      console.error('Error deleting API configuration:', error);
      showNotification('Error deleting API configuration', 'error');
    }
  };

  const handleNewApiNameChange = (event) => {
    setNewApiConfig({
      ...newApiConfig,
      name: event.target.value
    });
  };

  const handleNewApiBranchChange = (event) => {
    setNewApiConfig({
      ...newApiConfig,
      branchId: event.target.value
    });
  };

  const handleNewApiFieldChange = (index, field) => (event) => {
    const updatedFields = [...newApiConfig.fields];
    updatedFields[index][field] = event.target.value;
    setNewApiConfig({
      ...newApiConfig,
      fields: updatedFields
    });
  };

  const handleAddField = () => {
    setNewApiConfig({
      ...newApiConfig,
      fields: [...newApiConfig.fields, { name: '', value: '', visible: false }]
    });
  };

  const handleRemoveField = (index) => {
    const updatedFields = newApiConfig.fields.filter((_, i) => i !== index);
    setNewApiConfig({
      ...newApiConfig,
      fields: updatedFields
    });
  };

  const toggleFieldVisibility = (index) => {
    const updatedFields = [...newApiConfig.fields];
    updatedFields[index].visible = !updatedFields[index].visible;
    setNewApiConfig({
      ...newApiConfig,
      fields: updatedFields
    });
  };

  const handleAddApiConfig = async () => {
  // Validate form
  if (!newApiConfig.name || newApiConfig.fields.some(field => !field.name || !field.value)) {
    showNotification('Please fill in all required fields', 'error');
    return;
  }

  try {
    const apiData = {
      name: newApiConfig.name,
      branch_id: newApiConfig.branchId === 'all' ? null : newApiConfig.branchId,
      fields: newApiConfig.fields.map(field => ({
        field_name: field.name,
        field_value: field.value
      }))
    };
    
    const createdConfig = await apiClient.createAPIConfiguration(apiData);
    
    // Transform the response to match your current structure
    const transformedConfig = {
      id: createdConfig.id,
      name: createdConfig.name,
      branchId: createdConfig.branch_id || 'all',
      fields: createdConfig.fields.map(field => ({
        name: field.field_name,
        value: field.field_value,
        visible: false
      }))
    };
    
    setApiConfigs([...apiConfigs, transformedConfig]);
    setOpenAddApiDialog(false);
    
    // Reset form
    setNewApiConfig({
      name: '',
      branchId: 'all',
      fields: [{ name: 'API Key', value: '', visible: false }]
    });
    
    showNotification('New API configuration added successfully');
  } catch (error) {
    console.error('Error adding API configuration:', error);
    showNotification('Error adding API configuration', 'error');
  }
};

  // Mask API values
  const maskApiValue = (value, visible) => {
    if (visible) return value;
    if (value.length <= 8) return '•'.repeat(value.length);
    return `${value.substring(0, 4)}${'•'.repeat(value.length - 8)}${value.substring(value.length - 4)}`;
  };

  // API Configurations Component
  const APIConfigurations = () => (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ApiIcon />
          API Configurations
        </Typography>
        {!isEditingAPI ? (
          <Button 
            variant="outlined" 
            startIcon={<EditIcon />} 
            onClick={handleEditAPI}
          >
            Edit
          </Button>
        ) : null}
      </Box>

      <Paper elevation={isEditingAPI ? 2 : 0} sx={{ p: isEditingAPI ? 3 : 0 }}>
        <Grid container spacing={2}>
          {apiConfigs.map((config, configIndex) => (
            <Grid item xs={12} key={config.id}>
              <Box sx={{ 
                p: 2, 
                border: 1, 
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                bgcolor: 'background.default'
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">{config.name}</Typography>
                    <Chip 
                      label={getBranchName(config.branchId)} 
                      size="small" 
                      sx={{ 
                        bgcolor: config.branchId === 'all' ? '#ff9800' : '#2196f3', 
                        color: 'white'
                      }} 
                    />
                  </Box>
                  {isEditingAPI && (
                    <IconButton color="error" size="small" onClick={() => handleDeleteApiConfig(config.id)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {config.fields.map((field, fieldIndex) => (
                    <Box key={fieldIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={field.name} 
                        size="small" 
                        sx={{ 
                          bgcolor: '#e91e63', 
                          color: 'white',
                          width: '100px',
                          justifyContent: 'flex-start'
                        }} 
                      />
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                        {maskApiValue(field.value, field.visible)}
                      </Typography>
                      {isEditingAPI && (
                        <IconButton 
                          size="small" 
                          onClick={() => handleApiVisibility(configIndex, fieldIndex)}
                        >
                          {field.visible 
                            ? <VisibilityOffIcon fontSize="small" /> 
                            : <VisibilityIcon fontSize="small" />
                          }
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {isEditingAPI && (
          <>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddApiDialog(true)}
            >
              Add New API Configuration
            </Button>
            </Box>

            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancelAPI}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveAPISettings}
                color="primary"
              >
                Save API Settings
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );

  return (
    <Box>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab 
            icon={<BusinessIcon />} 
            label="Branches" 
            sx={{ minHeight: 64 }}
          />
          <Tab 
            icon={<TuneIcon />} 
            label="Variables" 
            sx={{ minHeight: 64 }}
          />
          <Tab 
            icon={<ApiIcon />} 
            label="API Configurations" 
            sx={{ minHeight: 64 }}
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      {tabValue === 0 && <Branches />}
      {tabValue === 1 && <Variables />}
      {tabValue === 2 && <APIConfigurations />}

      {/* Add API Config Dialog */}
      <Dialog 
        open={openAddApiDialog} 
        onClose={() => setOpenAddApiDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add New API Configuration</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="API Name"
                  value={newApiConfig.name}
                  onChange={handleNewApiNameChange}
                  variant="outlined"
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Applicable Branch</InputLabel>
                    <Select
                      value={newApiConfig.branchId}
                      label="Applicable Branch"
                      onChange={handleNewApiBranchChange}
                    >
                      <MenuItem value="all">All Branches</MenuItem>
                      {companyBranches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id}>
                          {branch.name} ({branch.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FormControl>
              </Grid>
            </Grid>
            
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>API Fields</Typography>
            <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
              {newApiConfig.fields.map((field, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: 'center' }}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Field Name"
                      value={field.name}
                      onChange={handleNewApiFieldChange(index, 'name')}
                      variant="outlined"
                      placeholder="e.g. API Key, Consumer Secret"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Value"
                      value={field.value}
                      onChange={handleNewApiFieldChange(index, 'value')}
                      variant="outlined"
                      type={field.visible ? "text" : "password"}
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => toggleFieldVisibility(index)}
                              edge="end"
                            >
                              {field.visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {index > 0 && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveField(index)}
                          fullWidth
                        >
                          <DeleteIcon fontSize="small" />
                        </Button>
                      )}
                      {index === newApiConfig.fields.length - 1 && (
                        <Button
                          variant="outlined"
                          color="success"
                          onClick={handleAddField}
                          fullWidth
                        >
                          <AddIcon fontSize="small" />
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddApiDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddApiConfig} 
            variant="contained" 
            color="primary"
          >
            Add Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AdminConfigurations;